import { useEffect, useRef, useState } from "react";
//utils
import { calculateBearing, haversineDistance, interpolateLatLng } from "../../utils/locationUtils";
import { findCurrentPigPosition } from "../../utils/pigUtils"; //, rotateIcon
//components
import { MemoizedMarker } from "./MemoizedMarker";
import { getColorChangingPigSvgIcon } from "./svg icons/svgPIG";
//hooks
import useHandleVisibilityChange from "../../hooks/useTabVisibility";
import useLiveTrackData from "../../hooks/useLiveTrackData";
import useLiveSiteMap from "../../hooks/useLiveSiteMap";
// import { useLoading } from "../../contexts/LoadingContext";

/**
 * Marks the POI on the map 
 * 
 * @param {DropdownOption} selectedJob
 * @param {Boolean} liveWPig
 * @param {DropdownOption} selectedRun
 * @param {Function} setAgmData - if agms from useLiveSiteMap change, update the agms in the parent component (MapComponent)
 * @param {Function} setDataUpdated - when checking for live track data every 10s interval, update parent, so live track summary can also update (so the pig and summary stay in sync)
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Map
 */
export const PigMarker = ({ 
  selectedJob, 
  liveWPig, 
  selectedRun, 
  setAgmData, 
  setDataUpdated ,
  mapRef
}) => {
  // const { showLoading, hideLoading } = useLoading();
  const { rawLiveTrackTable, fetchLiveTrackData } = useLiveTrackData();
  const {
    mergeLiveTrackWithRouteArr,
    checkLiveTrackForUpdates,
    findAndStoreLastUpdateValue,
    mergedLTandRoute,
    routeArrRef,
    fetchRouteArr,
    agms,
    // noData
  } = useLiveSiteMap();

  //On job Select - fetch the route array (api call and merge)
  useEffect(() => {
    if (selectedJob) {
      fetchRouteArr(selectedJob.value.toString());
    }
  }, [selectedJob]); //eslint-disable-line

  //On run select - fetch the live track data
  //side effects - agms
  useEffect(() => {
    if (selectedRun) {
      fetchLiveTrackData(selectedRun.value.toString());
    }
  }, [selectedRun]); //eslint-disable-line

  //merge the live track data into the route arr - route arr is a ref so doesn't change
  //Last update value - doesn't do anything at the moment
  //All activelivetrack data is returned from the API
  //Idea was to just return the data needed - but caused bugs so this tech debt at the moment
  //api call and api expect this value to be in there
  //this can be worked out of the project
  useEffect(() => {
    mergeLiveTrackWithRouteArr(rawLiveTrackTable);
    findAndStoreLastUpdateValue(rawLiveTrackTable);
  }, [rawLiveTrackTable]); //eslint-disable-line

  //Update the agm data in Map Component
  //**Using Context with useLiveSiteMap causes bugs and jumping**
  useEffect(() => {
    setAgmData(agms); //this comes from parent component (MapComponent.jsx)
  }, [agms]); //eslint-disable-line



  //This is a timed interval to make api calls to check for data every 10 seconds
  //future - could rework into web sockets 
  useEffect(() => {
    let interval = null;

    if (selectedRun) {
      interval = setInterval(() => {
        checkLiveTrackForUpdates(selectedRun.value.toString());

        setDataUpdated(new Date())
      }, 10000); // 10000 milliseconds = 10 seconds
    }
    // Cleanup function to clear the interval when the component unmounts or the selectedRun changes
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedRun]); //eslint-disable-line

  const [step, setStep] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [markerPosition, setMarkerPosition] = useState(null);

  const currentStepRef = useRef(0);
 
  const pigPositionSpeedAndStep = () => {    
    if (liveWPig) {
      const currentTime = new Date().getTime();
      const initialData = findCurrentPigPosition(mergedLTandRoute, currentTime); //util function to calculate the current position of PIG
      // if(!initialData) setSpeed(0);
      if(!initialData) {
        if(mergedLTandRoute.length && !mergedLTandRoute[0].PassTime){ //this assumes that there is no launch passtime
          setSpeed(0)
          setStep(0);
        }

      }
      if (initialData && initialData.position) {
        //if the pig is at receive calc the last rotation angle of the pig
        if (initialData.stepIndex === mergedLTandRoute.length - 1) {
          getLastRotationAngle(initialData.stepIndex);
        }
        setMarkerPosition(initialData.position);
        setStep(initialData.stepIndex);
        setSpeed(initialData.speed);
        currentStepRef.current = initialData.stepNumber;
      }
    }
  }

  //When tab becomes active -> recalculate PIG position
  useHandleVisibilityChange(pigPositionSpeedAndStep);

  //any changes to the LiveTrack and Route merge Arr - recalculate the position of the PIG
  useEffect(() => {    
    getLastRotationAngle();
    pigPositionSpeedAndStep();
  }, [mergedLTandRoute]); // eslint-disable-line

  const [rotationAngle, setRotationAngle] = useState(0); //angle of the PIG on map
  //if at the end of the route - keep the last calculated angle of the PIG
  //if not PIG will realign to orginal rotation angle
  const [lastValidRotationAngle, setLastValidRotationAngle] = useState(0);



  //this works but waits until second call/update of data from api
  const getLastRotationAngle = (stepIndex) => {
    if (stepIndex === mergedLTandRoute.length - 1) {
      let lastStart = mergedLTandRoute[mergedLTandRoute.length - 2];
      let lastEnd = mergedLTandRoute[mergedLTandRoute.length - 1];
      const newRotationAngle = calculateBearing(lastStart.lat, lastStart.lng, lastEnd.lat, lastEnd.lng);
      setRotationAngle(newRotationAngle); //calc rotation of pig
      setLastValidRotationAngle(newRotationAngle);
    }
  }

  //this is the animation hook
  useEffect(() => {
    if (!liveWPig || routeArrRef.current.length <= 1 || step >= routeArrRef.current.length) return;

    //testing here
    //Added 11-28-2024
    //Idea here is if the launch is cleared than it will set the PIG back to the Launch
    //If PIG starts acting weird -> it's probably this if statement doing it
    //!!!!!!!When Pause run comes into functionality than this will need to be adjusted
    // if (!speed || speed === 0) {
      // let noPassTimes = true;
      // for (let i = 0; i < routeArrRef.current.length; i++) {
      //   if (routeArrRef.current[i].PassTime) { noPassTimes = false };
      // }
      // if (noPassTimes) setStep(0);
    // }
    

    const start = routeArrRef.current[step];
    const end = step < routeArrRef.current.length - 1 ? routeArrRef.current[step + 1] : null;

    if (!end) {
      let lastStart = routeArrRef.current[routeArrRef.current.length - 2];
      let lastEnd = routeArrRef.current[routeArrRef.current.length - 1];
      const newRotationAngle = calculateBearing(lastStart.lat, lastStart.lng, lastEnd.lat, lastEnd.lng);
      setRotationAngle(newRotationAngle); //calc rotation of pig
      setLastValidRotationAngle(newRotationAngle);
    }

    //if there is a end position
    if (end) {
      //calculate the disance between points
      let distance = (haversineDistance(start.lat, start.lng, end.lat, end.lng) * 1000); // Convert km to meters
      if(end.Elong_Percent){
        distance = distance  * (1 + end.Elong_Percent / 100)
      }
      const metersPerSecond = (speed * 1000) / 3600;
      const travelTime = distance / metersPerSecond * 1000; //how long has the travel been
      const numSteps = Math.floor(travelTime / 100); // Update interval is 100 ms

      const intervalId = setInterval(() => {
        //testing here
        if(!speed || speed === 0) clearInterval(intervalId);

        if (currentStepRef.current < numSteps) {
          const fraction = currentStepRef.current / numSteps;
          const newPosition = interpolateLatLng(start, end, fraction); //find the lat and lng of pig using calcs
          
          setMarkerPosition(newPosition); //position of the pig on map

          const newRotationAngle = calculateBearing(start.lat, start.lng, end.lat, end.lng);
          setRotationAngle(newRotationAngle); //calc rotation of pig
          setLastValidRotationAngle(newRotationAngle);

          currentStepRef.current++;
        } else {
          clearInterval(intervalId);
          setStep(prevStep => prevStep + 1);
          currentStepRef.current = 0;  // Reset the current step reference
        }
        
      }, 100);

      return () => clearInterval(intervalId); // Cleanup interval on unmount or dependency change
    } else {
      // We are at the end of the route
      setMarkerPosition(start); //start is the last position in array
      setRotationAngle(lastValidRotationAngle); //last rotation angle that was calced
    }
  }, [step, routeArrRef.current, speed, liveWPig]); //eslint-disable-line

  return markerPosition && selectedRun ? (
    <MemoizedMarker
      position={markerPosition}
      animation={window.google.maps.Animation}
      zIndex={100}
      icon={{
        url: getColorChangingPigSvgIcon(rotationAngle),
        scaledSize: new window.google.maps.Size(48, 48),
        anchor: new window.google.maps.Point(24, 24), // Center the anchor
      }}
      visible={true}
      title={`Speed: ${speed} km/h`}
    />
  ) : null;
};
